import React, { useState, useRef } from "react";

/**
 * NetFeeBreakdownTooltip — hover-on-reveal showing the full math behind
 * a commission row: gross_fee − refunds − platform_cost = net_fee.
 *
 * Per BDC spec §Backend Logic "Net Fees = Fees Collected − Refunds −
 * Platform Costs (if applicable)". Surface it on every commission
 * display so AD/BDC can reconcile without opening a SQL console.
 *
 * Props:
 *   grossFee, refunds, platformCost, netFee — numbers (or string-coerced numbers)
 *   platformCostPct                          — optional, displayed as "%"
 *   currency                                 — optional label suffix; default ""
 *   children                                 — anchor element (the amount cell).
 */
function fmt(n) {
  const v = Number(n || 0);
  return Number.isFinite(v) ? v.toFixed(2) : "0.00";
}

function NetFeeBreakdownTooltip({
  grossFee,
  refunds,
  platformCost,
  platformCostPct,
  netFee,
  currency = "",
  children,
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const c = currency ? ` ${currency}` : "";
  return (
    <span
      ref={btnRef}
      tabIndex={0}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      style={{ position: "relative", cursor: "help", borderBottom: "1px dotted #6b7280" }}
      aria-describedby={open ? "net-fee-breakdown" : undefined}
    >
      {children}
      {open && (
        <span
          id="net-fee-breakdown"
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            background: "#111827",
            color: "#f9fafb",
            padding: "8px 10px",
            borderRadius: 5,
            fontSize: 12,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            fontWeight: "normal",
          }}
        >
          <strong>Net fees breakdown</strong>
          <table style={{ marginTop: 4, fontSize: 12, borderSpacing: 0 }}>
            <tbody>
              <tr>
                <td style={{ padding: "1px 8px 1px 0", color: "#9ca3af" }}>Gross fee</td>
                <td style={{ padding: "1px 0", textAlign: "right" }}>{fmt(grossFee)}{c}</td>
              </tr>
              <tr>
                <td style={{ padding: "1px 8px 1px 0", color: "#9ca3af" }}>− Refunds</td>
                <td style={{ padding: "1px 0", textAlign: "right" }}>{fmt(refunds)}{c}</td>
              </tr>
              <tr>
                <td style={{ padding: "1px 8px 1px 0", color: "#9ca3af" }}>
                  − Platform cost{platformCostPct !== undefined && platformCostPct !== null ? ` (${(Number(platformCostPct) * 100).toFixed(2)}%)` : ""}
                </td>
                <td style={{ padding: "1px 0", textAlign: "right" }}>{fmt(platformCost)}{c}</td>
              </tr>
              <tr>
                <td style={{ padding: "4px 8px 0 0", borderTop: "1px solid #374151", color: "#a7f3d0" }}>= Net fee</td>
                <td style={{ padding: "4px 0 0", textAlign: "right", borderTop: "1px solid #374151", color: "#a7f3d0", fontWeight: 600 }}>
                  {fmt(netFee)}{c}
                </td>
              </tr>
            </tbody>
          </table>
        </span>
      )}
    </span>
  );
}

export default NetFeeBreakdownTooltip;
